import type { BetaAccess, BetaUsage } from './analyticsApi';
import type { BetaFeatureSpec, MetricKind } from './betaFeatures';

export type RangeKey = '7' | '30' | '90' | 'all';

export type PeriodUnit = 'day' | 'week';

export type RangeSpec = {
  key: RangeKey;
  label: string;
  button: string;
  compare: string | null;
  days: number | null;
  unit: PeriodUnit;
};

export const RANGES: readonly RangeSpec[] = [
  { key: '7', label: 'Last 7 days', button: 'Last 7 days', compare: 'vs previous 7 days', days: 7, unit: 'day' },
  { key: '30', label: 'Last 30 days', button: '30 days', compare: 'vs previous 30 days', days: 30, unit: 'day' },
  { key: '90', label: 'Last 90 days', button: '90 days', compare: 'vs previous 90 days', days: 90, unit: 'week' },
  { key: 'all', label: 'All time', button: 'All time', compare: null, days: null, unit: 'week' },
];

export const DEFAULT_RANGE: RangeKey = '7';

/** Who the numbers count: every Insight user in a district, or only the people put on the beta list. */
export type CountKey = 'all' | 'list';

export const COUNTS: readonly { key: CountKey; button: string }[] = [
  { key: 'all', button: 'All users' },
  { key: 'list', button: 'Beta list' },
];

export const DEFAULT_COUNT: CountKey = 'all';

export function rangeSpec(key: RangeKey): RangeSpec {
  return RANGES.find((range) => range.key === key) ?? RANGES[0]!;
}

/** Inclusive ISO days; a null start is open-ended. */
export type DayRange = { start: string | null; end: string };

export type RangeBounds = { selected: DayRange; previous: DayRange | null };

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDate(day: string): Date {
  return new Date(`${day}T00:00:00Z`);
}

export function dayOf(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(day: string, count: number): string {
  return dayOf(new Date(utcDate(day).getTime() + count * DAY_MS));
}

export function mondayOf(day: string): string {
  const sinceMonday = (utcDate(day).getUTCDay() + 6) % 7;
  return addDays(day, -sinceMonday);
}

export function rangeBounds(key: RangeKey, asOf: Date): RangeBounds {
  const today = dayOf(asOf);
  const { days } = rangeSpec(key);
  if (days === null) return { selected: { start: null, end: today }, previous: null };
  const start = addDays(today, -(days - 1));
  return {
    selected: { start, end: today },
    previous: { start: addDays(start, -days), end: addDays(start, -1) },
  };
}

export function inRange(day: string, range: DayRange): boolean {
  return day <= range.end && (range.start === null || day >= range.start);
}

/**
 * Grants keyed by one sub per person: the sub their usage row carries when
 * they have history, else the first sub a grant named for them. Grants under
 * any other sign-in of theirs, or under their stable user id, fold onto it.
 */
export type AccessIndex = {
  grants: Map<string, Map<string, string>>;
  subsOf: Map<string, string[]>;
  userIdsOf: Map<string, string[]>;
};

export type Reconciled = { usage: BetaUsage; index: AccessIndex };

/**
 * Grants and usage rows are joined through every sub and user id relation the
 * snapshot and the access list carry, so a person whose history, grants and
 * current sign-in name different subs is one row with one set of numbers.
 */
export function reconcile(access: BetaAccess, usage: BetaUsage): Reconciled {
  const canonical = canonicalSubs(access, usage);
  const merged = new Map<string, BetaUsage['users'][number]>();
  for (const person of usage.users) {
    const sub = canonical.get(person.sub) ?? person.sub;
    const into = merged.get(sub);
    if (!into) {
      merged.set(sub, {
        ...person,
        sub,
        subs: [sub, ...person.subs.filter((alias) => alias !== sub)],
        days: person.days.map((day) => ({ day: day.day, counts: { ...day.counts } })),
      });
      continue;
    }
    for (const alias of person.subs) if (!into.subs.includes(alias)) into.subs.push(alias);
    if (!into.userId && person.userId) into.userId = person.userId;
    if (!into.districtId && person.districtId) into.districtId = person.districtId;
    for (const day of person.days) {
      const same = into.days.find((candidate) => candidate.day === day.day);
      if (!same) {
        into.days.push({ day: day.day, counts: { ...day.counts } });
        continue;
      }
      for (const [metric, count] of Object.entries(day.counts)) same.counts[metric] = (same.counts[metric] ?? 0) + count;
    }
    into.days.sort((a, b) => a.day.localeCompare(b.day));
  }
  const reconciled = { ...usage, users: [...merged.values()].sort((a, b) => a.sub.localeCompare(b.sub)) };
  return { usage: reconciled, index: accessIndex(access, reconciled, canonical) };
}

export function accessIndex(access: BetaAccess, usage?: BetaUsage, canonical = canonicalSubs(access, usage)): AccessIndex {
  const grants = new Map<string, Map<string, string>>();
  const subsOf = new Map<string, string[]>();
  const userIdsOf = new Map<string, string[]>();
  for (const entry of access.entries) {
    const sub = canonical.get(entry.sub) ?? entry.sub;
    const own = grants.get(sub) ?? new Map<string, string>();
    if (!own.has(entry.feature)) own.set(entry.feature, entry.grantedAt);
    grants.set(sub, own);
    const aliases = subsOf.get(sub) ?? [];
    if (!aliases.includes(entry.sub)) aliases.push(entry.sub);
    subsOf.set(sub, aliases);
    if (entry.userId) {
      const ids = userIdsOf.get(sub) ?? [];
      if (!ids.includes(entry.userId)) ids.push(entry.userId);
      userIdsOf.set(sub, ids);
    }
  }
  return { grants, subsOf, userIdsOf };
}

/**
 * Every sub and user id a person is known by is joined into one group before
 * any grant or usage row is read, so the result does not depend on the order
 * they arrive in. A group is named by the usage sub with the latest activity,
 * or failing any usage by its smallest sub.
 */
function canonicalSubs(access: BetaAccess, usage?: BetaUsage): Map<string, string> {
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    let root = key;
    while (parent.get(root) !== undefined && parent.get(root) !== root) root = parent.get(root)!;
    parent.set(key, root);
    return root;
  };
  const union = (a: string, b: string) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };
  const subKey = (sub: string) => `sub:${sub}`;
  const userKey = (userId: string) => `uid:${userId}`;
  const lastDay = new Map<string, string>();
  for (const person of usage?.users ?? []) {
    lastDay.set(person.sub, person.days[person.days.length - 1]?.day ?? '');
    find(subKey(person.sub));
    for (const alias of person.subs) union(subKey(alias), subKey(person.sub));
    if (person.userId) union(userKey(person.userId), subKey(person.sub));
  }
  for (const entry of access.entries) {
    find(subKey(entry.sub));
    if (entry.userId) union(userKey(entry.userId), subKey(entry.sub));
  }
  const members = new Map<string, string[]>();
  for (const key of parent.keys()) {
    if (!key.startsWith('sub:')) continue;
    const root = find(key);
    members.set(root, [...(members.get(root) ?? []), key.slice('sub:'.length)]);
  }
  const byPreference = (a: string, b: string) => {
    const usedA = lastDay.has(a);
    const usedB = lastDay.has(b);
    if (usedA !== usedB) return usedA ? -1 : 1;
    return (lastDay.get(b) ?? '').localeCompare(lastDay.get(a) ?? '') || a.localeCompare(b);
  };
  const canonical = new Map<string, string>();
  for (const subs of members.values()) {
    const name = [...subs].sort(byPreference)[0]!;
    for (const sub of subs) canonical.set(sub, name);
  }
  return canonical;
}

export function hasAccess(index: AccessIndex, sub: string, featureId: string): boolean {
  return index.grants.get(sub)?.has(featureId) ?? false;
}

export function accessCount(index: AccessIndex, featureId: string): number {
  let count = 0;
  for (const own of index.grants.values()) if (own.has(featureId)) count += 1;
  return count;
}

/** The subs a person's grants were made under, for finding them on the user list. */
export function grantSubs(index: AccessIndex, sub: string): string[] {
  return index.subsOf.get(sub) ?? [];
}

/** The stable user ids a person's grants recorded, for finding them on the user list once a sub is gone. */
export function grantUserIds(index: AccessIndex, sub: string): string[] {
  return index.userIdsOf.get(sub) ?? [];
}

export function metricByUser(usage: BetaUsage, metric: string, range: DayRange): Map<string, number> {
  const sums = new Map<string, number>();
  for (const person of usage.users) {
    let sum = 0;
    for (const day of person.days) if (inRange(day.day, range)) sum += day.counts[metric] ?? 0;
    sums.set(person.sub, sum);
  }
  return sums;
}

export function peopleWith(usage: BetaUsage, index: AccessIndex, featureId: string, metric: string, range: DayRange): number {
  let people = 0;
  for (const [sub, sum] of metricByUser(usage, metric, range)) if (sum > 0 && hasAccess(index, sub, featureId)) people += 1;
  return people;
}

export function totalOf(usage: BetaUsage, index: AccessIndex, featureId: string, metric: string, range: DayRange): number {
  let total = 0;
  for (const [sub, sum] of metricByUser(usage, metric, range)) if (hasAccess(index, sub, featureId)) total += sum;
  return total;
}

export function firstActivityDay(usage: BetaUsage, metrics: readonly string[]): string | null {
  let first: string | null = null;
  for (const person of usage.users) {
    for (const day of person.days) {
      if ((first === null || day.day < first) && metrics.some((metric) => (day.counts[metric] ?? 0) > 0)) first = day.day;
    }
  }
  return first;
}

export type Change = { kind: 'none' } | { kind: 'unknown' } | { kind: 'delta'; value: number };

export function changeOf(current: number, previous: number, bounds: RangeBounds, firstActivity: string | null): Change {
  if (bounds.previous === null) return { kind: 'none' };
  if (firstActivity === null || bounds.previous.end < firstActivity) return { kind: 'unknown' };
  return { kind: 'delta', value: current - previous };
}

export type Figure = { value: number; change: Change };

export function figureOf(
  usage: BetaUsage,
  index: AccessIndex,
  featureId: string,
  metric: string,
  kind: MetricKind,
  bounds: RangeBounds,
  firstActivity: string | null,
): Figure {
  const count = kind === 'people' ? peopleWith : totalOf;
  const value = count(usage, index, featureId, metric, bounds.selected);
  const previous = bounds.previous ? count(usage, index, featureId, metric, bounds.previous) : 0;
  return { value, change: changeOf(value, previous, bounds, firstActivity) };
}

export function chartPeriods(unit: PeriodUnit, selected: DayRange, usage?: BetaUsage): string[] {
  const periods: string[] = [];
  if (unit === 'day') {
    for (let day = selected.start ?? selected.end; day <= selected.end; day = addDays(day, 1)) periods.push(day);
    return periods;
  }
  const earliest = usage ? firstActivityDay(usage, allMetrics(usage)) : null;
  const first = mondayOf(selected.start ?? (earliest !== null && earliest <= selected.end ? earliest : selected.end));
  for (let week = first; week <= mondayOf(selected.end); week = addDays(week, 7)) periods.push(week);
  return periods;
}

function allMetrics(usage: BetaUsage): string[] {
  const metrics = new Set<string>();
  for (const person of usage.users) for (const day of person.days) for (const metric of Object.keys(day.counts)) metrics.add(metric);
  return [...metrics];
}

export function periodRange(start: string, unit: PeriodUnit, selected: DayRange): DayRange {
  const end = unit === 'day' ? start : addDays(start, 6);
  return {
    start: selected.start !== null && selected.start > start ? selected.start : start,
    end: end < selected.end ? end : selected.end,
  };
}

export function peopleActive(usage: BetaUsage, index: AccessIndex, feature: BetaFeatureSpec, range: DayRange): number {
  let people = 0;
  for (const person of usage.users) {
    if (!hasAccess(index, person.sub, feature.id)) continue;
    const active = person.days.some(
      (day) => inRange(day.day, range) && ((day.counts[feature.keyAction1] ?? 0) > 0 || (day.counts[feature.keyAction2] ?? 0) > 0),
    );
    if (active) people += 1;
  }
  return people;
}

export function peopleActiveSeries(
  usage: BetaUsage,
  index: AccessIndex,
  feature: BetaFeatureSpec,
  periods: string[],
  unit: PeriodUnit,
  selected: DayRange,
): number[] {
  return periods.map((start) => peopleActive(usage, index, feature, periodRange(start, unit, selected)));
}

const WEEKDAY_LABELS = 7;

function validDate(day: string): Date | null {
  const at = utcDate(day);
  return Number.isNaN(at.getTime()) ? null : at;
}

export function formatPeriodShort(day: string, unit: PeriodUnit, count: number): string {
  const at = validDate(day);
  if (!at) return day;
  if (unit === 'day' && count <= WEEKDAY_LABELS) {
    return `${at.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })} ${at.getUTCDate()}`;
  }
  return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function formatPeriod(day: string, unit: PeriodUnit): string {
  const at = validDate(day);
  if (!at) return day;
  if (unit === 'week') return `Week of ${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`;
  return at.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
